import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Divider,
  Alert,
  Paper,
} from "@mui/material";

import AppModal from "../common/AppModal";
import DetailItem from "../common/DetailItem";
import StatusChip from "../common/StatusChip";
import AppCircularProgress from "../common/AppCircularProgress";
import { StudentTermReportResponseDto } from "../../shared/dtos/studentTermReport/StudentTermReportResponseDto";

interface ViewStudentTermReportModalProps {
  open: boolean;
  onClose: () => void;
  report: StudentTermReportResponseDto | null;
  loading: boolean;
  error?: string | null;
}

const ViewStudentTermReportModal: React.FC<ViewStudentTermReportModalProps> = ({
  open,
  onClose,
  report,
  loading,
  error,
}) => {
  const renderContent = (): React.ReactNode => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            my: 5,
          }}
        >
          <AppCircularProgress />
          <Typography sx={{ ml: 2, color: "text.secondary" }}>
            Loading Report Card...
          </Typography>
        </Box>
      );
    }
    if (error) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    }
    if (!report) {
      return (
        <Typography
          sx={{ my: 2, textAlign: "center", color: "text.secondary" }}
        >
          No data available.
        </Typography>
      );
    }

    return (
      <Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            mb: 3,
          }}
        >
          <DetailItem label="Student" value={report.studentName || "N/A"} />
          <DetailItem
            label="Admission"
            value={report.admissionNumber || "N/A"}
          />
          <Box>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 0.5 }}
            >
              Mean Grade
            </Typography>
            <StatusChip status={report.meanGrade as any} />
          </Box>
          <DetailItem
            label="Position"
            value={`${report.classPosition || "-"} (Class)`}
          />
        </Box>

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ borderRadius: 1 }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "action.hover" }}>
                <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Score (%)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.subjectEntries?.map((entry) => (
                <TableRow key={entry.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {entry.subjectName}
                  </TableCell>
                  <TableCell>{entry.percentageScore}%</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{entry.grade}</TableCell>
                  <TableCell>{entry.subjectTeacherRemarks || "N/A"}</TableCell>
                </TableRow>
              ))}
              {(!report.subjectEntries ||
                report.subjectEntries.length === 0) && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    align="center"
                    sx={{ py: 3, color: "text.secondary" }}
                  >
                    No subject entries found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 1,
            bgcolor: "background.default",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, display: "block", mb: 0.5 }}
          >
            TEACHER REMARKS
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {report.classTeacherRemarks || "Pending..."}
          </Typography>

          <Divider sx={{ my: 1.5 }} />

          <Typography
            variant="caption"
            sx={{ fontWeight: 700, display: "block", mb: 0.5 }}
          >
            PRINCIPAL REMARKS
          </Typography>
          <Typography variant="body2">
            {report.principalRemarks || "Pending..."}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Academic Performance Report"
      width="md"
    >
      {renderContent()}
    </AppModal>
  );
};

export default ViewStudentTermReportModal;
